import { useState } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { format, parseISO } from "date-fns";
import { Trash2, Loader2, X } from "lucide-react";
import { IconTote, IconPin, IconHourglass } from "@/components/icons";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useCartContext } from "@/contexts/CartContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import { useLocalizedDate } from "@/hooks/use-localized-date";
import type { CartItem } from "@/hooks/use-cart";
import { toast } from "sonner";

export function CartDrawer() {
  const { items, itemCount, removeItem, loading } = useCartContext();
  const [open, setOpen] = useState(false);
  const { formatPrice } = useCurrency();
  const { t } = useUiTranslations();
  const { locale } = useLocalizedDate();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const estimatedTotal = items.reduce((sum, item) => {
    const snap = item.unit_price_snapshot ?? 0;
    if (item.experiences.pricing_model === "PER_GROUP") return sum + snap;
    return sum + (snap * item.adult_count) + (snap * 0.5 * item.child_count);
  }, 0);

  async function handleRemove(id: string) {
    setRemovingId(id);
    try {
      await removeItem(id);
      toast.success(t("cart.itemRemoved"));
    } catch {
      toast.error(t("cart.removeFailed"));
    } finally {
      setRemovingId(null);
    }
  }

  const formatTime = (tm: string) => tm.slice(0, 5);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="relative flex items-center justify-center h-10 w-10 rounded-full hover:bg-accent transition-colors"
          aria-label={`Cart with ${itemCount} items`}
        >
          <IconTote className="h-5 w-5 text-foreground" />
          {itemCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              {itemCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <IconTote className="h-5 w-5" />
            {t("cart.title")}
            {itemCount > 0 && (
              <Badge variant="secondary" className="ml-1">{itemCount}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {loading && items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
            <IconTote className="h-12 w-12 text-muted-foreground/40" />
            <p className="text-muted-foreground">{t("cart.empty")}</p>
            <Button variant="outline" onClick={() => setOpen(false)} asChild>
              <LocalizedLink to="/experiences">{t("cart.browseExperiences")}</LocalizedLink>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 pr-1 mt-4">
              {items.map((item) => (
                <CartItemCard
                  key={item.id}
                  item={item}
                  onRemove={() => handleRemove(item.id)}
                  removing={removingId === item.id}
                  formatPrice={formatPrice}
                  formatTime={formatTime}
                  locale={locale}
                />
              ))}
            </div>

            <Separator className="my-4" />

            <div className="space-y-3 pb-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("cart.estimatedTotal")}</span>
                <span className="font-semibold text-foreground text-lg">{formatPrice(estimatedTotal)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t("cart.dynamicPriceNote")}
              </p>
              <Button
                className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 font-semibold min-h-[48px]"
                onClick={() => setOpen(false)}
                asChild
              >
                <LocalizedLink to="/checkout">{t("cart.proceedToCheckout")}</LocalizedLink>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CartItemCard({
  item,
  onRemove,
  removing,
  formatPrice,
  formatTime,
  locale,
}: {
  item: CartItem;
  onRemove: () => void;
  removing: boolean;
  formatPrice: (n: number) => string;
  formatTime: (t: string) => string;
  locale: Locale;
}) {
  const exp = item.experiences;
  const slot = item.slots;
  const heroImage = exp.images?.[0] || "/placeholder.svg";
  const unitPrice = item.unit_price_snapshot ?? 0;

  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2">
      <div className="flex gap-3">
        <img
          src={heroImage}
          alt={exp.title}
          className="h-16 w-16 rounded-md object-cover shrink-0"
        />
        <div className="flex-1 min-w-0">
          <LocalizedLink
            to={`/experiences/${(exp as any).city_slug ?? "unknown"}/${exp.slug}`}
            className="text-sm font-medium text-foreground hover:underline line-clamp-1"
          >
            {exp.title}
          </LocalizedLink>
          <p className="text-xs text-muted-foreground mt-0.5">
            {format(parseISO(slot.date), "EEE d MMM", { locale })} · {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
          </p>
          <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
            {exp.location && (
              <span className="flex items-center gap-0.5">
                <IconPin className="h-3 w-3" />
                {exp.location}
              </span>
            )}
            <span className="flex items-center gap-0.5">
              <IconHourglass className="h-3 w-3" />
              {exp.duration_minutes}m
            </span>
          </div>
        </div>
        <button
          onClick={onRemove}
          disabled={removing}
          className="shrink-0 h-8 w-8 flex items-center justify-center rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
          aria-label="Remove item"
        >
          {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {item.adult_count} adult{item.adult_count !== 1 ? "s" : ""}
          {item.child_count > 0 && `, ${item.child_count} child${item.child_count !== 1 ? "ren" : ""}`}
          {item.toddler_count > 0 && `, ${item.toddler_count} toddler${item.toddler_count !== 1 ? "s" : ""}`}
        </span>
        <span className="font-medium text-foreground">
          {exp.pricing_model === "PER_GROUP" ? formatPrice(unitPrice) : `${formatPrice(unitPrice)} × ${item.adult_count}`}
        </span>
      </div>
    </div>
  );
}

import type { Locale } from "date-fns";
